/**
 * Metadata builder class for defining component metadata.
 */

import { Arg } from './args';
import { generateValidationsFromMetadata } from '../../../component-validations';
import { sortAndStampMeta, sortKeys } from './helpers';

// Cache for validations, keyed by Metadata instance
const VALIDATIONS_CACHE = new WeakMap();

/**
 * Metadata builder class for defining component metadata.
 * Provides a fluent API and static factory methods.
 */
export class Metadata {
  #config = {};
  #frozen = false;

  constructor(config = {}) {
    this.#config = {
      component: config.component ?? null,
      description: config.description ?? '',
      name: config.name ?? '',
      meta: {},
      omits: config.omits ?? [],
      resources: {},
      ...config,
    };

    // Process meta args if provided
    if (config.meta) {
      this.#config.meta = this.#processMeta(config.meta);
    }
  }

  // ============ Static Factory Methods ============

  /**
   * Creates a new Metadata builder with the given name.
   * @param {string} name - Component name
   * @returns {Metadata}
   */
  static create(name) {
    return new Metadata({ name });
  }

  /**
   * Defines metadata from a configuration object.
   * @param {Object} config - Metadata configuration
   * @param {string} config.name - Component name
   * @param {string} [config.description] - Component description
   * @param {Array<Object>} [config.extends] - Parent metadata to extend
   * @param {Object} [config.meta] - Argument definitions
   * @param {Array<string>} [config.omits] - Argument names to drop from parent metadata in setMetadata flow (before merge)
   * @returns {Metadata}
   */
  static define(config) {
    const metadata = new Metadata({
      ...config,
      meta: {}, // Will be processed after extends
    });

    // Process extends first
    if (config.extends) {
      const extendsList = Array.isArray(config.extends) ? config.extends : [config.extends];
      metadata.extend(...extendsList);
    }

    // Then add own meta (overrides extended)
    if (config.meta) {
      metadata.args(config.meta);
    }

    return metadata.freeze();
  }

  /**
   * Converts a module URL to a component path.
   * @param {string} url - The module URL from import.meta.url
   * @returns {string|null} - Component path (e.g., 'Spds::Core::Button')
   */
  static fromUrl(url) {
    if (!url) return null;
    const match = url.match(/components\/(.+)\.(gjs|gts|js|ts)$/);
    if (!match) return null;

    return match[1]
      .split('/')
      .map(segment =>
        segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join('')
      )
      .join('::');
  }

  /**
   * Merges parent and child metadata objects.
   * Child properties take precedence over parent properties.
   * @param {Object} parent - Parent metadata object
   * @param {Object} child - Child metadata object
   * @returns {Object} Merged metadata object
   */
  static #merge(parent = { meta: {} }, child = { meta: {} }) {
    return {
      ...child,
      meta: {
        ...parent?.meta,
        ...child?.meta,
      },
      resources: {
        ...parent?.resources,
        ...child?.resources,
      },
    };
  }

  /**
   * Converts a value to a plain object representation.
   * @param {Object|Metadata} value - Value to convert
   * @returns {Object}
   */
  static #toObject(value) {
    return value instanceof Metadata ? value.toObject() : value;
  }

  /**
   * Merges a config object with parent class metadata.
   * Applies configObj.omits to parent meta before merging (drops those argument names).
   * @param {Object} configObj - The config object to merge into
   * @param {Object} ParentClass - The parent class with metadata
   * @returns {Object} - Merged config object
   */
  static #mergeWithParent(configObj, ParentClass) {
    const parentMeta = ParentClass?.metadata;

    if (!parentMeta) return configObj;

    const { meta, resources } = Metadata.#toObject(parentMeta);
    const parentFiltered = {
      meta: this.#removeOmittedArgs(meta, configObj.omits),
      resources,
    };
    const merged = Metadata.#merge(parentFiltered, configObj);

    return { ...configObj, meta: merged.meta, resources: merged.resources };
  }

  /**
   * Returns a copy of meta with the given argument names removed.
   * Used in the setMetadata flow to drop inherited args before merging with child metadata.
   * @param {Object} meta - Parent meta object (arg name -> arg config)
   * @param {Array<string>} [omits] - Argument names to remove
   * @returns {Object} New meta object with omitted keys removed (shallow copy of the rest)
   */
  static #removeOmittedArgs(meta, omits) {
    if (!omits?.length) return meta;

    const filteredMeta = { ...meta };

    [...new Set(omits)].forEach(omit => {
      delete filteredMeta[omit];
    });

    return filteredMeta;
  }

  /**
   * Creates metadata for a component, merging with parent metadata.
   * This is the main entry point for component metadata setup.
   * @param {Object|Metadata} config - Metadata configuration or Metadata instance
   * @param {string} moduleUrl - The module URL from import.meta.url
   * @param {Object} ParentClass - The parent class to inherit metadata from
   * @returns {Metadata}
   */
  static forComponent(config, moduleUrl, ParentClass) {
    const configObj = Metadata.#mergeWithParent(Metadata.#toObject(config), ParentClass);
    const metadata = new Metadata(configObj);

    metadata.#config.component = Metadata.fromUrl(moduleUrl);
    metadata.#config.meta = sortAndStampMeta(metadata.#config.meta, metadata.#config.component);

    return metadata.freeze({ skipSort: true });
  }

  // ============ Builder Methods ============
  // Note: Builder methods use "set" prefix to avoid conflict with getters

  /**
   * Sets the component name.
   * @param {string} value - Component name
   * @returns {Metadata}
   */
  setName(value) {
    this.#ensureMutable();
    this.#config.name = value;
    return this;
  }

  /**
   * Sets the component description.
   * @param {string} value - Description text
   * @returns {Metadata}
   */
  setDescription(value) {
    this.#ensureMutable();
    this.#config.description = value;
    return this;
  }

  /**
   * Adds a single argument definition.
   * @param {string} name - Argument name
   * @param {Arg|Object} argConfig - Argument configuration
   * @returns {Metadata}
   */
  arg(name, argConfig) {
    this.#ensureMutable();
    this.#config.meta[name] = argConfig instanceof Arg ? argConfig.toConfig() : argConfig;
    return this;
  }

  /**
   * Adds multiple argument definitions.
   * @param {Object} argsMap - Map of argument names to configurations
   * @returns {Metadata}
   */
  args(argsMap) {
    this.#ensureMutable();
    for (const [name, config] of Object.entries(argsMap)) {
      this.arg(name, config);
    }
    return this;
  }

  /**
   * Adds a resource link.
   * @param {string} name - Resource name
   * @param {string} url - Resource URL
   * @returns {Metadata}
   */
  resource(name, url) {
    this.#ensureMutable();
    this.#config.resources[name] = url;
    return this;
  }

  /**
   * Adds a GitHub resource link.
   * @param {string} url - GitHub URL
   * @returns {Metadata}
   */
  github(url) {
    return this.resource('github', url);
  }

  /**
   * Extracts meta from a parent (Metadata instance or plain object).
   * @param {Object|Metadata} parent - Parent to extract from
   * @returns {Object} - The meta object
   */
  static #extractMeta(parent) {
    return parent instanceof Metadata ? parent.meta : (parent?.meta ?? parent);
  }

  /**
   * Extends this metadata with parent metadata.
   * @param {...Object|Metadata} parents - Parent metadata objects
   * @returns {Metadata}
   */
  extend(...parents) {
    this.#ensureMutable();

    for (const parent of parents) {
      this.#config.meta = { ...this.#config.meta, ...Metadata.#extractMeta(parent) };

      const { resources } = parent;

      if (resources) {
        this.#config.resources = { ...this.#config.resources, ...resources };
      }
    }

    return this;
  }

  // ============ Finalization ============

  /**
   * Freezes the metadata to prevent further modifications.
   * Sorts meta keys alphabetically for consistent ordering.
   * @returns {Metadata}
   */
  freeze(options = { skipSort: false }) {
    if (this.#config.meta && !options.skipSort) {
      this.#config.meta = sortKeys(this.#config.meta);
    }

    this.#frozen = true;

    return this;
  }

  // ============ Accessors ============

  get name() {
    return this.#config.name;
  }

  get description() {
    return this.#config.description;
  }

  get meta() {
    return this.#config.meta;
  }

  get resources() {
    return this.#config.resources;
  }

  get component() {
    return this.#config.component;
  }

  // ============ Derived Properties ============

  /**
   * Gets or generates the GitHub URL.
   * @returns {string|null}
   */
  get githubUrl() {
    if (this.#config.resources.github) return this.#config.resources.github;
    if (!this.component) return null;

    const path = this.component
      .replace(/::/g, '/')
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .toLowerCase();
    return `https://github.com/simplepractice/ember-simplepractice/tree/staging/addon/src/components/${path}.gjs`;
  }

  // ============ Value Helpers ============

  /**
   * Gets the default value for a specific argument.
   * Mirrors getDefaultValueFromMetadata functionality.
   * @param {string} argName - Name of the argument
   * @returns {*} The default value for the argument
   */
  getDefault(argName) {
    return this.#config.meta[argName]?.defaultValue;
  }

  /**
   * Gets all default values as an object.
   * @returns {Object} Map of argument names to default values
   */
  getDefaults() {
    return Object.fromEntries(
      Object.entries(this.#config.meta).map(([key, config]) => [key, config.defaultValue])
    );
  }

  /**
   * Gets all argument names.
   * @returns {Array<string>} Array of argument names
   */
  get argNames() {
    return Object.keys(this.#config.meta);
  }

  /**
   * Gets initial values for documentation, preferring initialValue over defaultValue.
   * @returns {Object} Map of argument names to initial/default values
   */
  getInitialValues() {
    return Object.fromEntries(
      Object.entries(this.#config.meta).map(([key, config]) => [
        key,
        config.initialValue ?? config.defaultValue,
      ])
    );
  }

  /**
   * Gets metadata filtered to only include public arguments (non-private).
   * @returns {Object} Meta object with only public arguments
   */
  get publicMeta() {
    return Object.fromEntries(
      Object.entries(this.#config.meta)
        .filter(([, config]) => !config.private)
        .map(([key, config]) => [key, { ...config }])
    );
  }

  /**
   * Gets initial values for public (non-private) arguments only.
   * @returns {Object} Map of public argument names to initial/default values
   */
  get publicInitialValues() {
    return Object.fromEntries(
      Object.entries(this.publicMeta).map(([key, config]) => [
        key,
        config.initialValue ?? config.defaultValue,
      ])
    );
  }

  // ============ Validation Integration ============

  /**
   * Gets cached validations for this metadata.
   * @returns {Array}
   */
  get validations() {
    if (!VALIDATIONS_CACHE.has(this)) {
      VALIDATIONS_CACHE.set(this, generateValidationsFromMetadata(this.toObject()));
    }
    return VALIDATIONS_CACHE.get(this);
  }

  // ============ Serialization ============

  /**
   * Converts the Metadata to a plain object (backwards compatible format).
   * @returns {Object}
   */
  toObject() {
    return {
      component: this.#config.component,
      description: this.#config.description,
      name: this.#config.name,
      meta: { ...this.#config.meta },
      omits: this.#config.omits ?? [],
      resources: {
        github: this.githubUrl,
        ...this.#config.resources,
      },
    };
  }

  // ============ Private Helpers ============

  #ensureMutable() {
    if (this.#frozen) {
      throw new Error('Cannot modify frozen Metadata');
    }
  }

  #processMeta(meta) {
    return Object.fromEntries(
      Object.entries(meta).map(([key, value]) => [
        key,
        value instanceof Arg ? value.toConfig() : value,
      ])
    );
  }
}

export default Metadata;
